import {
    useState,
    useEffect,
    useRef,
    forwardRef,
    useImperativeHandle,
    useCallback,
    useMemo,
} from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faSearch,
    faXmark,
} from "@fortawesome/free-solid-svg-icons";
import "../style/common/DropDownV3.scss";

import { AutoSizer, CellMeasurer, CellMeasurerCache, List } from "react-virtualized";
import { Checkbox, FormControlLabel } from "@mui/material";
import { ArrowUp } from "../assets/Icons/ArrowUp";
import { Arrow } from "../assets/Icons/arrow";
import { focusElemnt } from "../utils/helpers";
import { useDebounce } from "../utils/useDebounce";
const DropDownV3 = forwardRef((props, ref) => {
    const {
        width,
        listWidth = width,
        name,
        label,
        isCompulsory,
        options,
        preSelectedValues = [],
        searchPlaceholder,
        disabled,
        remoteData = false,
        selectAllOption = false,
        direction = "down",
        directionX = 'right',
        defaultAllSelected = false,
        id,
        showCrossIcon = false,
        searchByCode = false,
        searchByName = "",
        labelKey,
        primaryKey,
        multiSelect = false,
        isObject = false,
        onSelect = () => { },
        nextId,
        prevId,
        multipleLable = false,
        isPortal = false
    } = props;
    const dropRef = useRef(null);
    const searchInputRef = useRef(null);
    const virtualizedListRef = useRef(null);
    const [showDrop, setShowDrop] = useState(false);
    const [list, setList] = useState([]);
    const [selectedDisplayValue, setSelectedDisplayValue] = useState("");
    const [selectedValues, setSelectedValues] = useState([]);
    const [searchInput, setSearchInput] = useState("");
    const [remoteSearchInput, setRemoteSearchInput] = useState("");
    const [filteredLists, setFilteredLists] = useState([]);
    const [triggeredScroll, setTriggeredScroll] = useState(false);
    const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: width });

    const debouncedSearchTerm2 = useDebounce(searchInput, 500);
    const [allSelected, setAllSelected] = useState(defaultAllSelected);
    const [cursor, setCursor] = useState(-1);
    const input1ref = useRef(null);
    const listRef = useRef(null);
    const [listHeight, setListHeight] = useState(0);

    const uniqueOptions = useMemo(() => {
        let opt = [...options]
        if (!Array.isArray(opt)) {
            return [];
        }
        if (selectAllOption && opt?.length !== 0) {
            opt.unshift({ [labelKey]: "All Select", [primaryKey]: 0 });
        }
        return Array.from(
            new Map(opt.map((obj) => [obj[primaryKey], obj])).values()
        );
    }, [options, primaryKey, selectAllOption]);

    useEffect(() => {
        if (filteredLists.length === 0) {
            setListHeight(30);
        } else {
            let totalHeight = 0;
            for (let i = 0; i < filteredLists.length; i++) {
                totalHeight += cache.current.rowHeight({ index: i }) || 40;
            }

            setListHeight(Math.max(30, Math.min(totalHeight, 200)));

        }
    }, [filteredLists, showDrop]);

    useEffect(() => {
        if (showDrop && isPortal) updateDropdownPos();
    }, [showDrop]);


    const updateDropdownPos = () => {
        if (!dropRef.current) return;
        const rect = dropRef.current.getBoundingClientRect();
        setDropdownPos({
            top: rect.bottom + window.scrollY + 6,
            left: rect.left + window.scrollX,
            width: width === '100%' ? rect.width : listWidth,
        });
    };
    useEffect(() => {
        const handleScroll = () => {
            if (showDrop && isPortal) updateDropdownPos();
        };
        window.addEventListener("scroll", handleScroll, true);
        window.addEventListener("resize", handleScroll);
        return () => {
            window.removeEventListener("scroll", handleScroll, true);
            window.removeEventListener("resize", handleScroll);
        };
    }, [showDrop]);


    useEffect(() => {
        setFilteredLists(uniqueOptions)
        setList(uniqueOptions)
    }, [uniqueOptions]);

    useEffect(() => {
        cache.current.clearAll();
        if (virtualizedListRef.current) {
            virtualizedListRef.current.recomputeRowHeights();
            virtualizedListRef.current.forceUpdateGrid(); // <- important
        }
    }, [filteredLists]);


    useEffect(() => {
        let preValueUnique = Array.from(
            new Map(preSelectedValues.map((obj) => [obj[primaryKey], obj])).values()
        );
        let optList = uniqueOptions
        if (selectAllOption) {
            optList = uniqueOptions?.filter((obj) => obj?.[primaryKey] !== 0)
        }
        if (preSelectedValues?.length > 1 && (optList?.length === preValueUnique?.length)) {
            setAllSelected(true);
            handleSelectValue(uniqueOptions, `All ${labelKey}`)
        } else {
            let preSelectedValuesWithAll = preSelectedValues.filter((obj) => obj?.[primaryKey] !== 0)
            if ((preSelectedValues?.length > 1 && selectAllOption) && (optList?.length === preSelectedValuesWithAll?.length)) {
                setAllSelected(true);
                handleSelectValue(uniqueOptions, `All ${labelKey}`, true)
            } else {
                setAllSelected(false);
                handleSelectValue(preValueUnique, preValueUnique?.length > 0 ? preValueUnique?.[0]?.[labelKey] : '')
            }
        }
    }, [preSelectedValues, uniqueOptions, selectAllOption])

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [dropRef]);

    useEffect(() => {
        searchItems(debouncedSearchTerm2);
    }, [debouncedSearchTerm2]);

    const handleClickOutside = (e) => {
        if (!(dropRef && dropRef.current && dropRef.current.contains(e.target))) {
            setShowDrop(false);
            setSearchInput('')
        }
    };

    const handleDrop = (e) => {
        setSearchInput("");
        setShowDrop(true);
    };

    const searchItems = (searchValue) => {
        if (remoteData) {
            setRemoteSearchInput(searchValue);
            return;
        }
        setSearchInput(searchValue);

        if (searchValue?.toString()?.trim() !== "") {
            let filterData = [];

            if (searchByName) {
                filterData = list.filter((item) => {
                    return item[props.searchByName].toLowerCase() === searchValue.toLowerCase();
                });
            }

            if (filterData.length === 0) {
                if (searchByCode) {
                    filterData = list.filter((item) => {
                        return item[props.labelKey]
                            .toLowerCase()
                            .includes(searchValue.toLowerCase());
                    });
                } else {
                    filterData = list.filter((item) => {
                        return item[props.labelKey]
                            .toLowerCase()
                            .includes(searchValue.toLowerCase());
                    });
                }
            }
            setFilteredLists(filterData);
            setCursor(0);
        } else {
            setFilteredLists(uniqueOptions);
        }
    };

    const clearSearchInput = () => {
        if (searchInputRef.current) {
            searchInputRef.current.value = "";
            searchItems("");
        }
    };

    const resetItem = () => {
        const { onRemove } = props;
        setSelectedDisplayValue("");
        setSelectedValues([]);
        if (onRemove) onRemove([]);
        setAllSelected(false);
    };

    const isSelectedValue = (item) => {
        const { isObject, primaryKey } = props;
        if (isObject) {
            return (
                selectedValues.filter((i) => i[primaryKey] === item[primaryKey])
                    .length > 0
            );
        }
        return selectedValues.filter((i) => i === item).length > 0;
    };

    useImperativeHandle(ref, () => ({
        resetAutocomplete(event) {
            resetItem();
        },
    }));



    const labelTxyt = isCompulsory ? (
        <span>
            {label}
            <span style={{ color: "red", paddingLeft: "5px" }}>*</span>
        </span>
    ) : (
        label
    );
    const showSuggestion = (e, el) => {
        e.stopPropagation();
        setShowDrop(true);
        searchInputRef?.current?.focus();
    };
    const handleSearchKeyDown = (e) => {
        if (e.key === 'Tab') {
            e.stopPropagation();
            setShowDrop(false);
            searchItems('')
        }
        if (showDrop && (e.altKey && (e.key?.toLowerCase() === 'a' || e.key?.toLowerCase() === 't'))) {
            setShowDrop(false);
        }
    };
    const cache = useRef(
        new CellMeasurerCache({
            fixedWidth: true,
            defaultHeight: 30,
            minHeight: 30,
        })
    );



    const handleItemClick = (event, item) => {
        event.stopPropagation();
        let newSelectedValues;
        if (selectAllOption && item?.[primaryKey] === 0) {
            if (selectedValues.length === list.length) {
                handleSelectValue([], "");
                onSelect([], event);
                setSearchInput('')
            } else {
                handleSelectValue(list, `All ${labelKey}`, true);
                onSelect(list, event);
                setSearchInput('')
            }
            // handleSelectValue(list, `All ${labelKey}`)
            // onSelect(list)
        } else {
            if (multiSelect) {
                const isSelected = selectedValues.some(selected => selected[primaryKey] === item[primaryKey]);
                if (selectAllOption && isSelected) {
                    newSelectedValues = isSelected
                        ? selectedValues.filter(selected => selected[primaryKey] !== item[primaryKey] && selected[primaryKey] !== 0)
                        : [...selectedValues, item];
                } else {
                    newSelectedValues = isSelected
                        ? selectedValues.filter(selected => selected[primaryKey] !== item[primaryKey])
                        : [...selectedValues, item];
                }
                setSelectedValues(newSelectedValues);
                if (!isObject && newSelectedValues?.length > 1) {
                } else {
                    setSelectedDisplayValue(newSelectedValues.map(selected => selected[labelKey]).join(', '));
                }
            } else {
                newSelectedValues = [item];
                setSelectedValues(newSelectedValues);
                setSelectedDisplayValue(item[labelKey]);
                handleShowDrop(false);
            }
            onSelect(newSelectedValues, event);
            setSearchInput("");

        }
        setFilteredLists(list)
    }


    const handleSelectValue = (item, label, specficLabel = false) => {
        setSelectedValues(item);
        if ((multiSelect && multipleLable) && !specficLabel) {
            setSelectedDisplayValue(item.map((obj) => obj?.[labelKey]).join(','))
        } else {
            setSelectedDisplayValue(label);
        }
    }
    //
    const handleShowDrop = (value) => {
        setShowDrop(value);
    }



    const searchNavigation = (event) => {
        if (filteredLists.length === 0) return;
        let newCursor = cursor;
        if (showDrop && (event.altKey && (event.key?.toLowerCase() === 'a' || event.key?.toLowerCase() === 't'))) {
            setShowDrop(false);
        }
        switch (event.key) {
            case 'ArrowDown':
                event.stopPropagation();
                newCursor = cursor < filteredLists.length - 1 ? cursor + 1 : 0;
                setCursor(newCursor);
                if (virtualizedListRef.current) {
                    virtualizedListRef.current.scrollToRow(newCursor);
                }
                event.preventDefault();
                break;

            case 'ArrowUp':
                event.stopPropagation();
                newCursor = cursor > 0 ? cursor - 1 : filteredLists.length - 1;
                setCursor(newCursor);
                if (virtualizedListRef.current) {
                    virtualizedListRef.current.scrollToRow(newCursor);
                }
                event.preventDefault();
                break;

            case 'Enter':
                event.stopPropagation();
                if (cursor >= 0 && cursor < filteredLists.length) {
                    handleItemClick(event, filteredLists[cursor]);
                    handleShowDrop(false);
                    focusElemnt(nextId)
                    event.preventDefault();
                }
                break;

            case 'Escape':
                event.stopPropagation();
                if (cursor >= 0 && cursor < filteredLists.length) {
                    handleShowDrop(false);
                    focusElemnt(prevId)
                    event.preventDefault();
                }
                break;
            default:
        }

    };



    const defaultProps = () => {
        return {
            multiSelect,
            searchInputRef,
            name,
            searchPlaceholder,
            searchItems,
            setCursor,
            setTriggeredScroll,
            searchNavigation,
            searchInput,
            remoteSearchInput,
            clearSearchInput,
            listRef,
            filteredLists,
            virtualizedListRef,
            listHeight,
            cache,
            setListHeight,
            triggeredScroll,
            cursor,
            isSelectedValue,
            handleItemClick,
            multiSelect,
            mainProps: props,
        }
    }



    return (
        <div
            className={`dropdown--wrapperV3 ${disabled ? "disable" : ""}`}
            style={{ width: width }}
            ref={dropRef}
            onClick={handleDrop}
            onKeyDown={handleSearchKeyDown}

        >
            <input
                type="text"
                className={`inp--field ${disabled ? "disable" : ""} ${showDrop ? "focusColor" : ""
                    }`}
                onFocus={(e) => {
                    if (!disabled) {
                        if (selectedValues.length === 1) {
                            const selected = selectedValues[0];
                            const index = filteredLists.findIndex(
                                (item) => item[primaryKey] === selected[primaryKey]
                            );
                            setCursor(index !== -1 ? index : 1);
                        } else {
                            if (selectedValues?.length > 1 && selectAllOption) {
                                setCursor(-1);
                            } else {
                                setCursor(1)
                            }
                        }
                        showSuggestion(e)
                        setTriggeredScroll(false)
                    }
                }}
                value={allSelected ? `All ${label}` : selectedDisplayValue}
                readOnly
                id={id}
                ref={input1ref}
                onClick={showSuggestion}
            // onKeyDown={keyboardNavigation}
            />
            {showCrossIcon && (selectedDisplayValue && !disabled) ? (
                <div className="cross--icon" onClick={resetItem}>
                    <span className="cross" style={{ display: showCrossIcon ? 'block' : 'none' }}>
                        <FontAwesomeIcon icon={faXmark} color={"#666666"} />
                    </span>
                </div>
            ) : null}
            <div className="caret--icon">{showDrop ? <ArrowUp fill='#0085FE' /> : <Arrow />}</div>
            <div
                className={`inp--label ${showDrop ? "focus--label" : ""} ${selectedDisplayValue ? " filled" : ""
                    }`}
            >
                {labelTxyt}
            </div>
            {showDrop &&
                <>{
                    isPortal ?
                        <div className="dropdown--wrapperV3">
                            <div
                                className={`drop--wrapper ${directionX === 'right' ? 'right' : 'left'}`}
                                style={{
                                    position: "fixed",
                                    top: dropdownPos.top,
                                    left: dropdownPos.left,
                                    width: dropdownPos.width,
                                    zIndex: 9999,
                                }}
                            >
                                <ShowDropDownV3
                                    {...defaultProps()}
                                />
                            </div>
                        </div>
                        :
                        (
                            <div
                                className={`drop--wrapper ${direction === "up" ? "up" : "down"} ${directionX === 'right' ? 'right' : 'left'}`}
                                style={{ width: listWidth }}
                            >
                                <ShowDropDownV3
                                    {...defaultProps()}
                                />
                            </div>

                        )
                }
                </>
            }
        </div >
    );
});

export default DropDownV3;



export const ShowDropDownV3 = (props) => {
    const {
        searchInputRef,
        name,
        searchPlaceholder,
        searchItems = () => { },
        setCursor = () => { },
        setTriggeredScroll = () => { },
        searchNavigation = () => { },
        searchInput = '',
        remoteSearchInput = '',
        clearSearchInput = () => { },
        listRef = '',
        filteredLists = [],
        virtualizedListRef,
        listHeight = 0,
        cache,
        setListHeight,
        triggeredScroll,
        cursor,
        isSelectedValue,
        handleItemClick,
        mainProps,
        multiSelect = false
    } = props

    const RowRenderer = ({ index, key, style, parent }) => {
        const {
            isObject,
            labelKey,
            primaryKey,
        } = mainProps;
        const obj = filteredLists[index];
        const isSelected = isSelectedValue(obj);
        const optionKey = isObject ? obj[primaryKey] : `${obj}-${index}`;
        const optionLabel = isObject ? obj[labelKey] : obj;
        return (
            <CellMeasurer
                key={key}
                cache={cache.current}
                parent={parent}
                columnIndex={0}
                rowIndex={index}
            >
                {({ measure }) => (

                    <li
                        key={key}
                        style={{ ...style, whiteSpace: 'normal' }}
                    >
                        <div
                            key={optionKey}
                            className={`drop--item ${isSelected ? "selected" : ""} ${cursor === index ? "highlighted" : ""
                                }`}
                            onClick={(e) => handleItemClick(e, obj)}
                        >
                            {multiSelect ? <Checkbox checked={isSelected === true} /> : null}
                            <div onLoad={measure}>{optionLabel}</div>
                        </div>
                    </li>

                )}
            </CellMeasurer>
        );
    }
    return (
        <>
            <div className="drop--search">
                <input
                    name={name}
                    type="text"
                    className="search"
                    placeholder={searchPlaceholder}
                    onChange={(e) => {
                        searchItems(e.target.value)
                        setCursor(0)
                        setTriggeredScroll(true)
                    }}
                    ref={searchInputRef}
                    autoFocus
                    onFocus={() => {
                        setTimeout(() => {
                            setTriggeredScroll(true)
                        }, 0)
                    }}
                    autoComplete={"off"}
                    onKeyDown={searchNavigation}
                />
                <div className="search--icon">
                    <FontAwesomeIcon icon={faSearch} color={"#777"} />
                </div>
                {(searchInput || remoteSearchInput) && (
                    <div className="cross--icon" onClick={clearSearchInput}>
                        <span className="cross">
                            <FontAwesomeIcon icon={faXmark} color={"#777"} />
                        </span>
                    </div>
                )}
            </div>
            <div className="drop--content">
                <ul ref={listRef} className="list-group scroll" style={{ ...(filteredLists.length > 0 ? { height: `${listHeight}px` } : { minHeight: '30px' }), overflowY: 'auto' }}>
                    {filteredLists.length > 0 ? (
                        <AutoSizer >
                            {({ height, width }) => (
                                <List
                                    ref={virtualizedListRef}
                                    width={width - 4}
                                    height={listHeight}
                                    rowCount={filteredLists.length}
                                    rowHeight={cache.current.rowHeight}
                                    rowRenderer={RowRenderer}
                                    deferredMeasurementCache={cache.current}
                                    overscanRowCount={5}
                                    style={{ overflowX: 'hidden', }}
                                    onRowsRendered={() => {
                                        let totalHeight = 0;
                                        for (let i = 0; i < filteredLists.length; i++) {
                                            totalHeight += cache.current.getHeight(i, 0) || 40;
                                        }
                                        const maxHeight = 200;
                                        setListHeight(Math.min(totalHeight, maxHeight));
                                    }}
                                    scrollToIndex={triggeredScroll ? undefined : cursor}
                                />
                            )}
                        </AutoSizer>
                    ) : (
                        <li>
                            <div className="drop--item">
                                <div>No Data Found</div>
                            </div>
                        </li>
                    )}
                </ul>
            </div>
        </>
    )
}